import type { CarrierCode } from "@flexfreight/core";
import { Badge, Banner, Button, CarrierLogo, DataTable, Menu, MoreIcon, Modal, notify, PageHeader, Section, StatusBadge, statusBadge, TextField, Truncate, useConfirm, type Column, type MenuEntry } from "@flexfreight/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { EagleLoadCard } from "./SigninProviders";
import { CarrierLogosSection, type CarrierLogoEntry } from "./CarrierLogos";

export type HouseCarrier = {
  carrier: string;
  name: string;
  sandboxAvailable: boolean;
  source: string;
  fields: { key: string; label: string; secret: boolean; required: boolean; hint?: string; requiredUnless?: string[] }[];
  mode: "mock" | "sandbox" | "live";
  enabled: boolean;
  credentials: { fieldsSet: string[]; complete: boolean; updatedAt: string | null; updatedBy: string | null };
  lastTest: { at: string; ok: boolean | null; mode?: "mock" | "sandbox" | "live" | null; message: string } | null;
  /** Setup problems to fix (e.g. Old Dominion live without a billing address). Older API builds omit it. */
  warnings?: string[];
};

const KEY = ["console", "carriers"] as const;
const dateTime = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** Credential state as an `integration` status-map key. */
function credentialState(c: HouseCarrier): string {
  if (c.credentials.fieldsSet.length === 0) return "not_set";
  if (!c.credentials.complete) return "incomplete";
  if (c.lastTest?.ok) return "connected";
  if (c.lastTest?.ok === false) return "test_failed";
  if (c.lastTest && c.lastTest.ok === null) return "unverified";
  return "untested";
}

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/** Admin Center > Carriers & API keys. Credentials are write-only: the API never sends a value back. */
export function CarriersPage() {
  const me = useConsoleMe();
  const canManage = me.capabilities.includes("carriers:manage");
  const canGoLive = me.capabilities.includes("carriers:go_live");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<{ carriers: HouseCarrier[] }>("/v1/console/carriers") });
  // Each carrier's logo: an uploaded official file (Carrier logos, below) wins over the bundled one.
  const logos = useQuery({ queryKey: ["console", "carrier-logos"], queryFn: () => api<{ logos: CarrierLogoEntry[] }>("/v1/console/carrier-logos") });
  const logoFor = (code: string) => logos.data?.logos.find((l) => l.code === code && l.source === "upload")?.logoUrl ?? undefined;
  const [editing, setEditing] = useState<HouseCarrier | null>(null);
  const [goingLive, setGoingLive] = useState<HouseCarrier | null>(null);
  const { confirm, dialog } = useConfirm();

  const setOne = (c: HouseCarrier) =>
    qc.setQueryData<{ carriers: HouseCarrier[] }>(KEY, (old) => (old ? { carriers: old.carriers.map((x) => (x.carrier === c.carrier ? c : x)) } : old));

  const test = useMutation({
    mutationFn: ({ c, mode }: { c: HouseCarrier; mode: "sandbox" | "live" }) => api<HouseCarrier>(`/v1/console/carriers/${c.carrier}/test`, { method: "POST", body: { mode } }),
    onSuccess: (c) => {
      setOne(c);
      if (c.lastTest?.ok) notify.success(`${c.name} connected`);
      else if (c.lastTest?.ok === null) notify.info(`${c.name} keys saved, not verified`, c.lastTest.message);
      else notify.warning(`${c.name}: not connected`, c.lastTest?.message);
    },
    onError: (e) => notify.error("Test didn't run", message(e)),
  });

  const patch = useMutation({
    mutationFn: ({ c, body }: { c: HouseCarrier; body: Record<string, unknown> }) => api<HouseCarrier>(`/v1/console/carriers/${c.carrier}`, { method: "PATCH", body }),
    onSuccess: (c) => {
      setOne(c);
      notify.success(`${c.name} updated`, `${statusBadge("carrierMode", c.mode).label}${c.enabled ? "" : ", turned off"}`);
    },
    onError: (e) => notify.error("Couldn't update the carrier", message(e)),
  });

  const remove = useMutation({
    mutationFn: (c: HouseCarrier) => api<HouseCarrier>(`/v1/console/carriers/${c.carrier}/credentials`, { method: "DELETE" }),
    onSuccess: (c) => {
      setOne(c);
      notify.success(`${c.name} credentials removed`, "The carrier is back in mock mode.");
    },
    onError: (e) => notify.error("Couldn't remove credentials", message(e)),
  });

  async function askRemove(c: HouseCarrier) {
    if (await confirm({ title: `Remove ${c.name} credentials?`, body: "Quoting with this carrier goes back to mock rates until new credentials are added.", confirmLabel: "Remove", danger: true })) {
      remove.mutate(c);
    }
  }

  const carriers = q.data?.carriers ?? [];
  const live = carriers.filter((c) => c.mode === "live").length;
  const warned = carriers.filter((c) => c.warnings?.length);

  const menu = (c: HouseCarrier): MenuEntry[] => {
    const keys = c.credentials.fieldsSet.length > 0;
    const liveReady = c.credentials.complete && !!c.lastTest?.ok && c.lastTest.mode === "live";
    return [
      ...(c.sandboxAvailable ? [{ label: "Test sandbox", disabled: !keys, onSelect: () => test.mutate({ c, mode: "sandbox" }) }] : []),
      ...(canGoLive ? [{ label: "Test live", disabled: !keys, onSelect: () => test.mutate({ c, mode: "live" }) }] : []),
      { type: "separator" as const, key: "mode" },
      ...(c.sandboxAvailable && c.mode !== "sandbox" ? [{ label: "Use sandbox", disabled: !c.credentials.complete, onSelect: () => patch.mutate({ c, body: { mode: "sandbox" } }) }] : []),
      ...(c.mode !== "mock" ? [{ label: "Back to mock", onSelect: () => patch.mutate({ c, body: { mode: "mock" } }) }] : []),
      ...(canGoLive && c.mode !== "live" ? [{ label: liveReady ? "Go live" : "Go live (needs a passing live test)", disabled: !liveReady, onSelect: () => setGoingLive(c) }] : []),
      { label: c.enabled ? "Turn off" : "Turn on", onSelect: () => patch.mutate({ c, body: { enabled: !c.enabled } }) },
      ...(keys ? [{ type: "separator" as const, key: "danger" }, { label: "Remove keys", danger: true, onSelect: () => void askRemove(c) }] : []),
    ];
  };

  const busy = (c: HouseCarrier) => (test.isPending && test.variables?.c.carrier === c.carrier) || (patch.isPending && patch.variables?.c.carrier === c.carrier);
  const columns: Column<HouseCarrier>[] = [
    { key: "carrier", header: "Carrier", primary: true, truncate: "custom", text: (c) => c.name, render: (c) => <CarrierLogo code={c.carrier as CarrierCode} name={c.name} size="sm" fallback="monogram" logoUrl={logoFor(c.carrier)} />, sub: (c) => c.source },
    {
      key: "mode",
      header: "Mode",
      width: "9rem",
      render: (c) => (
        <span className="ffx-cellrow">
          <StatusBadge domain="carrierMode" value={c.mode} size="sm" />
          {!c.enabled ? <StatusBadge domain="integration" value="Off" size="sm" /> : null}
        </span>
      ),
    },
    {
      key: "keys",
      header: "Keys",
      width: "13rem",
      render: (c) => <StatusBadge domain="integration" value={credentialState(c)} size="sm" />,
      sub: (c) => (c.credentials.updatedAt ? `Updated ${dateTime.format(new Date(c.credentials.updatedAt))}${c.credentials.updatedBy ? ` by ${c.credentials.updatedBy}` : ""}` : "No keys saved"),
    },
    {
      key: "test",
      header: "Last test",
      width: "28%",
      mobile: "hide",
      truncate: "custom",
      render: (c) => (c.lastTest ? <Truncate text={`${c.lastTest.mode ? `${statusBadge("carrierMode", c.lastTest.mode).label}: ` : ""}${c.lastTest.message}`} /> : <span className="ffa-muted">Never tested</span>),
      sub: (c) => (c.lastTest ? <time dateTime={c.lastTest.at}>{dateTime.format(new Date(c.lastTest.at))}</time> : null),
      subText: (c) => (c.lastTest ? dateTime.format(new Date(c.lastTest.at)) : ""),
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: "Actions",
            align: "end" as const,
            width: "12rem",
            hideable: false,
            render: (c: HouseCarrier) => (
              <span className="ffa-rowactions">
                <Button size="sm" onClick={() => setEditing(c)}>
                  {c.credentials.fieldsSet.length ? "Replace keys" : "Add keys"}
                </Button>
                <Menu look="icon" size="sm" disabled={busy(c)} trigger={<MoreIcon />} triggerLabel={`More actions for ${c.name}`} items={menu(c)} />
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <section className="ffa-page">
      <PageHeader
        eyebrow="Admin Center"
        title="Carriers & API keys"
        subtitle="FlexFreight's own carrier connections. Keys are encrypted on the server and never shown again after you save them."
        badges={q.data ? <Badge tone={live > 0 ? "success" : "neutral"}>{live > 0 ? `${live} live` : "All carriers in mock"}</Badge> : null}
      />

      {warned.length ? (
        <Banner tone="warning" title="Setup to finish">
          <ul className="ffa-plainlist">
            {warned.flatMap((c) => (c.warnings ?? []).map((w) => <li key={`${c.carrier}:${w}`}>{w}</li>))}
          </ul>
        </Banner>
      ) : null}

      {me.capabilities.includes("settings:read") ? <EagleLoadCard canManage={me.capabilities.includes("integrations:manage")} canGoLive={me.capabilities.includes("carriers:go_live")} /> : null}

      <Section title="Direct carrier connections" description="Quoting and booking switch to these settings as soon as you save them. Mock never contacts a carrier, and while the server's CARRIER_MODE is set to mock every carrier stays in mock." flush>
        <DataTable
          caption="Carriers"
          columns={columns}
          rows={carriers}
          rowKey={(c) => c.carrier}
          maxHeight="none"
          loading={q.isPending}
          error={q.isError ? { title: "Couldn't load carriers", message: message(q.error), onRetry: () => void q.refetch() } : null}
          empty="No carriers are set up."
          rowTone={(c) => (c.warnings?.length ? "warning" : null)}
        />
      </Section>

      <CarrierLogosSection canManage={canManage} />

      {editing ? <CredentialsDialog carrier={editing} onClose={() => setEditing(null)} onSaved={(c) => setOne(c)} /> : null}
      {goingLive ? <GoLiveDialog carrier={goingLive} onClose={() => setGoingLive(null)} onDone={(c) => setOne(c)} /> : null}
      {dialog}
    </section>
  );
}

function CredentialsDialog({ carrier, onClose, onSaved }: { carrier: HouseCarrier; onClose: () => void; onSaved: (c: HouseCarrier) => void }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () => {
      const credentials = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== ""));
      return api<HouseCarrier>(`/v1/console/carriers/${carrier.carrier}/credentials`, { method: "PUT", body: { credentials } });
    },
    onSuccess: (c) => {
      onSaved(c);
      notify.success(`${c.name} keys saved`, c.credentials.complete ? "Run a test to check the connection." : "Some required fields are still missing.");
      onClose();
    },
    onError: (e) => notify.error("Couldn't save keys", message(e)),
  });
  const filled = Object.values(values).some((v) => v.trim() !== "");
  function submit(e: FormEvent) {
    e.preventDefault();
    if (filled) save.mutate();
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={`${carrier.name} API keys`}
      description="Fill in only what you want to add or change. Saved values are never shown again."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="ffa-cred-form" loading={save.isPending} disabled={!filled}>
            Save keys
          </Button>
        </>
      }
    >
      <form id="ffa-cred-form" className="ffa-form" onSubmit={submit} autoComplete="off">
        {carrier.fields.map((fl) => {
          const isSet = carrier.credentials.fieldsSet.includes(fl.key);
          return (
            <TextField
              key={fl.key}
              label={fl.label}
              type={fl.secret ? "password" : "text"}
              autoComplete={fl.secret ? "new-password" : "off"}
              spellCheck={false}
              optional={!fl.required || (fl.requiredUnless ?? []).some((k) => (values[k] ?? "").trim() !== "" || carrier.credentials.fieldsSet.includes(k))}
              hint={isSet ? "Saved. Leave blank to keep it." : fl.hint}
              value={values[fl.key] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [fl.key]: e.target.value }))}
            />
          );
        })}
      </form>
    </Modal>
  );
}

function GoLiveDialog({ carrier, onClose, onDone }: { carrier: HouseCarrier; onClose: () => void; onDone: (c: HouseCarrier) => void }) {
  const phrase = `GO LIVE ${carrier.carrier.toUpperCase()}`;
  const [typed, setTyped] = useState("");
  const go = useMutation({
    mutationFn: () => api<HouseCarrier>(`/v1/console/carriers/${carrier.carrier}`, { method: "PATCH", body: { mode: "live", confirm: typed } }),
    onSuccess: (c) => {
      onDone(c);
      notify.success(`${c.name} is live`, "Quotes and bookings will use the real carrier.");
      onClose();
    },
    onError: (e) => notify.error("Didn't go live", message(e)),
  });
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={`Go live with ${carrier.name}?`}
      description="Real rates and real bookings. Tests and demonstrations must stay in mock or sandbox."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" loading={go.isPending} disabled={typed !== phrase} onClick={() => go.mutate()}>
            Go live
          </Button>
        </>
      }
    >
      <TextField label={`Type ${phrase} to confirm`} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
    </Modal>
  );
}
